/**
 * Setting the FEATit app up for a deployment in one click (F-0237, `docs/concept-mobile-app.md`).
 *
 * A deployment says who it is at `/.well-known/featit`, and hands out a setup link, a QR code and a
 * setup file. All three carry **public configuration only**: the deployment's origin and its id.
 * The app verifies them by fetching `/.well-known/featit` from that origin and comparing the id, and
 * then signs in through the deployment's own page (F-0228). No token, code or credential ever
 * travels in a link or a file, because a link can be read by whoever the device hands it to.
 */

/** Where a deployment describes itself, at the root of its origin. */
export const WELL_KNOWN_FEATIT_PATH = '/.well-known/featit';

/** The web page a setup link lands on when the app is not installed. */
export const APP_SETUP_PATH = '/app-setup';

/** The setup file's media type, and the extension it is saved with. */
export const APP_SETUP_FILE_TYPE = 'application/vnd.featit.setup+json';
export const APP_SETUP_FILE_EXTENSION = 'featit';

/** Raised when the shape of `/.well-known/featit` or the setup file changes incompatibly. */
export const APP_SETUP_CONTRACT = 1;

/** What `/.well-known/featit` answers. */
export interface IFeatitWellKnown {
  readonly product: 'featit';
  readonly contract: number;
  /** Stable for the deployment's life; a restored or re-created deployment has a new one. */
  readonly deploymentId: string;
  /** The canonical origin people open. A pasted page URL resolves to this. */
  readonly origin: string;
  /** What the app shows instead of an address. */
  readonly name: string;
  readonly iconUrl: string;
  readonly apiPath: string;
  readonly signInPath: string;
  readonly setupPath: string;
  /**
   * Whether this deployment accepts sign-ups (F-0333, ADR-151 §9): the app shows *Create account*
   * only when it does. `mode !== 'closed'` and nothing more: `POST /auth/register` already tells
   * anyone who tries whether sign-up is open, and the domain restriction list stays unpublished.
   */
  readonly registrationOpen: boolean;
  /**
   * T-0488, AC-W19a: which of the three sign-up screens a visitor without an account sees. Read it
   * through {@link signUpModeOf}, which answers for a deployment that predates the field too. Like
   * `registrationOpen`, it says nothing about which domains are accepted.
   */
  readonly signUp?: SignUpModeType;
  /**
   * I-0983: where the iOS app is installed from, when this deployment names one
   * (`FEATIT_IOS_APP_STORE_URL`). Absent, `/app-setup` still copies its link before an install.
   */
  readonly appStoreUrl?: string;
  /**
   * F-0554, AC-TA7: the website this deployment's legal pages are on (`FEATIT_WEBSITE_ORIGIN`). Read
   * the three addresses through {@link legalLinks}. Absent from a deployment that predates it.
   */
  readonly websiteOrigin?: string;
  /**
   * T-0615, AC-W19c: the website's early-access form, present only when this deployment names its
   * website (`FEATIT_WEBSITE_ORIGIN`) — unlike `websiteOrigin`, never a development fallback, since
   * it is where a closed deployment sends visitors. Absent, a closed deployment's screens say to ask
   * whoever runs it. Read the words through {@link signUpClosedCopy}.
   */
  readonly earlyAccessUrl?: string;
  /**
   * F-0554: the terms version in force (`TERMS_VERSION`), for a client that signs people up without
   * this build's shared package — a script, a native screen. Absent from an older deployment.
   */
  readonly termsVersion?: string;
  /**
   * PX-14 (D-0430, ADR-191): whether a customer's first tap on their invitation accepts the terms,
   * beside the line that says so (`FIRST_TAP_TERMS_WORDING`). Off, the terms are their own step
   * after the tap. Absent from an older deployment: read as off.
   */
  readonly guestFirstTapAcceptsTerms?: boolean;
}

/**
 * What a visitor with no account is offered (T-0488, AC-W19a).
 *
 * - `open`: the sign-up form — anyone may try, or some addresses need no invitation.
 * - `invitation`: no form; joining takes an invitation link from someone already here.
 * - `closed`: no form and no invitation either; an administrator creates accounts.
 *
 * Kept apart from `registrationOpen` because "no" has two different next steps, and a closed
 * deployment that told its visitors to ask for an invitation would send them after a link that
 * cannot work.
 */
export const SIGN_UP_MODES = {
  OPEN: 'open',
  INVITATION: 'invitation',
  CLOSED: 'closed',
} as const;

export type SignUpModeType = (typeof SIGN_UP_MODES)[keyof typeof SIGN_UP_MODES];

/**
 * The sign-up mode a deployment states, or — from one that predates `signUp` — what its
 * `registrationOpen` meant then: before invitation-only existed, "not open" could only be closed.
 */
export function signUpModeOf(
  identity: Pick<IFeatitWellKnown, 'registrationOpen' | 'signUp'>,
): SignUpModeType {
  if (identity.signUp && (Object.values(SIGN_UP_MODES) as string[]).includes(identity.signUp)) {
    return identity.signUp;
  }
  return identity.registrationOpen ? SIGN_UP_MODES.OPEN : SIGN_UP_MODES.CLOSED;
}

/** Everything a setup link or file may say. Public, and nothing else. */
export interface IAppSetup {
  readonly origin: string;
  readonly deploymentId: string;
  /**
   * F-0332: the pairing request a *Connect a mobile device* QR code names. Links only, never the
   * setup file. A request id, not a credential: it lets one device register itself, once, within two
   * minutes, for approval by the person who opened the request (ADR-151 §2).
   */
  readonly pairRequestId?: string;
}

/** The query parameters each form of the link may carry. A guard holds the builders to these. */
export const APP_SETUP_LINK_PARAMETERS = {
  universal: ['deployment'],
  app: ['origin', 'deployment'],
} as const;

/** F-0332: the one parameter a pairing link adds to either form. Never anything credential-shaped. */
export const APP_SETUP_PAIR_PARAMETER = 'pair';

/** A pairing request id is a uuid; anything else in `pair` is not one, and is dropped. */
const PAIR_REQUEST_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The fields the setup file may carry. */
export const APP_SETUP_FILE_FIELDS = [
  'product',
  'contract',
  'origin',
  'deploymentId',
  'name',
] as const;

export interface IAppSetupLinks {
  /** A universal link on the deployment's own domain: opens the app, or `/app-setup` without it. */
  readonly universal: string;
  /** The `featit://` form, for a page that knows the app is installed. */
  readonly app: string;
  /** What a QR code encodes: the universal link, so a camera without the app still lands somewhere. */
  readonly qr: string;
}

export function appSetupLinks(setup: IAppSetup): IAppSetupLinks {
  const origin = setupOrigin(setup.origin);
  const universal = new URL(APP_SETUP_PATH, origin);
  universal.searchParams.set('deployment', setup.deploymentId);
  const app = new URL('featit://setup');
  app.searchParams.set('origin', origin);
  app.searchParams.set('deployment', setup.deploymentId);
  if (setup.pairRequestId) {
    universal.searchParams.set(APP_SETUP_PAIR_PARAMETER, setup.pairRequestId);
    app.searchParams.set(APP_SETUP_PAIR_PARAMETER, setup.pairRequestId);
  }
  return { universal: universal.toString(), app: app.toString(), qr: universal.toString() };
}

export interface IAppSetupFile {
  readonly product: 'featit';
  readonly contract: number;
  readonly origin: string;
  readonly deploymentId: string;
  readonly name: string;
}

export function appSetupFile(setup: IAppSetup & { readonly name: string }): IAppSetupFile {
  return {
    product: 'featit',
    contract: APP_SETUP_CONTRACT,
    origin: setupOrigin(setup.origin),
    deploymentId: setup.deploymentId,
    name: setup.name,
  };
}

/**
 * The setup a link names, or `null` for anything that is not one. Reads both forms. The origin of
 * a universal link is its own; a link cannot point the app at a deployment on another domain.
 * A `pair` that is not a request id is dropped rather than carried.
 */
export function readAppSetupLink(link: string): IAppSetup | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  const deploymentId = url.searchParams.get('deployment')?.trim();
  if (!deploymentId) return null;
  const pair = readPairRequestId(url.searchParams.get(APP_SETUP_PAIR_PARAMETER));
  const pairing = pair ? { pairRequestId: pair } : {};
  if (url.protocol === 'featit:') {
    if (url.hostname !== 'setup' && url.pathname.replace(/^\/+/, '') !== 'setup') return null;
    const origin = url.searchParams.get('origin');
    try {
      return origin ? { origin: setupOrigin(origin), deploymentId, ...pairing } : null;
    } catch {
      return null;
    }
  }
  if ((url.protocol !== 'https:' && url.protocol !== 'http:') || url.pathname !== APP_SETUP_PATH) {
    return null;
  }
  return { origin: url.origin, deploymentId, ...pairing };
}

/** The pairing request id a link's `pair` names, or null when it is absent or not one. */
export function readPairRequestId(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && PAIR_REQUEST_ID_PATTERN.test(trimmed) ? trimmed.toLowerCase() : null;
}

/**
 * I-1048 (AC-QR14): what a pasted clipboard holds, as every client sorts it. The iPhone's pairing
 * screen refused the desktop's own setup link as "no FEATit connection link" because it took only
 * a pairing link, and it decided what a link was by its own rules; this is the one classification,
 * and `fixtures/pasted-setup.v1.json` holds its cases and words for the clients that cannot import
 * it (FeatItKit's `PastedSetup`, later Android).
 */
export const PASTED_SETUP_KINDS = {
  empty: 'empty',
  notALink: 'not-a-link',
  foreignLink: 'foreign-link',
  setupLink: 'setup-link',
  pairingLink: 'pairing-link',
} as const;

export type PastedSetupKindType = (typeof PASTED_SETUP_KINDS)[keyof typeof PASTED_SETUP_KINDS];

export type PastedSetupType =
  | { readonly kind: 'empty'; readonly refusal: string }
  | { readonly kind: 'not-a-link'; readonly refusal: string }
  | { readonly kind: 'foreign-link'; readonly refusal: string; readonly host: string }
  | { readonly kind: 'setup-link'; readonly setup: IAppSetup }
  | {
      readonly kind: 'pairing-link';
      readonly setup: IAppSetup & { readonly pairRequestId: string };
    };

/** Each refusal names what was found; the foreign one names where the link goes. */
export const PASTED_SETUP_REFUSALS = {
  empty: 'The clipboard is empty.',
  notALink: "That isn't a link.",
  foreignLink: (host: string) => `That link goes to ${host}, not to FEATit.`,
} as const;

/**
 * What a clipboard holds, sorted into one of five kinds. A clipboard can carry several entries (a
 * URL and its text, say): the first that is a FEATit setup or pairing link wins; with none, the
 * first entry that is not blank decides the refusal. A link is an absolute URL with a scheme and
 * no whitespace inside it; anything else is not one, however a URL parser might resolve it against
 * a base.
 */
export function classifyPastedSetup(texts: readonly string[]): PastedSetupType {
  const entries = texts.map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  if (entries.length === 0) return { kind: 'empty', refusal: PASTED_SETUP_REFUSALS.empty };
  for (const entry of entries) {
    const setup = asLink(entry) ? readAppSetupLink(entry) : null;
    if (setup?.pairRequestId) {
      return { kind: 'pairing-link', setup: { ...setup, pairRequestId: setup.pairRequestId } };
    }
    if (setup) return { kind: 'setup-link', setup };
  }
  const url = asLink(entries[0]);
  if (url === null) return { kind: 'not-a-link', refusal: PASTED_SETUP_REFUSALS.notALink };
  const host = url.host || url.protocol.replace(/:$/, '');
  return { kind: 'foreign-link', refusal: PASTED_SETUP_REFUSALS.foreignLink(host), host };
}

/** The entry as an absolute URL with a scheme and no whitespace, or null when it is not a link. */
function asLink(text: string): URL | null {
  if (/\s/.test(text) || !/^[a-z][a-z0-9+.-]*:/i.test(text)) return null;
  try {
    return new URL(text);
  } catch {
    return null;
  }
}

/** An http(s) origin, and nothing after it. */
function setupOrigin(value: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error(`A setup origin is http or https, not ${url.protocol}`);
  }
  return url.origin;
}
